/**
 * Sticky page header.
 *
 * Replaces `UTTopbar` for the redesign: an eyebrow naming the sidebar group, the page title, and
 * a right-hand slot each screen fills with its own controls — the tenant filter, on screens that
 * list per BU. The signed-in user sits at the far right.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import type { JSX, ReactNode } from 'react';

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-base);
  padding: 0 var(--space-l);
  height: var(--layout-header-height);
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
`;

const titleBlockStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const eyebrowStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const titleStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
`;

const spacerStyle = css`
  flex: 1;
`;

const actionsStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
`;

const userStyle = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: var(--space-sm);
  border-left: 1px solid var(--color-border);
`;

const avatarStyle = css`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-round);
  background: var(--color-primary-subtle);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  flex-shrink: 0;
`;

const userTextStyle = css`
  display: flex;
  flex-direction: column;
`;

const userNameStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const userRoleStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

/** First letter of the first two words, e.g. "Rafael Lima" → "RL". */
export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
}

export interface PageHeaderProps {
  eyebrow: string;
  title: string;
  /** Screen-specific controls, shown left of the user block. */
  actions?: ReactNode;
  user?: {
    name: string;
    /** Role name, shown under the user's name. Omitted while it is still loading. */
    role?: string;
  };
}

export const PageHeader = ({ eyebrow, title, actions, user }: PageHeaderProps): JSX.Element => (
  <header className={headerStyle}>
    <div className={titleBlockStyle}>
      <span className={eyebrowStyle}>{eyebrow}</span>
      <span className={titleStyle}>{title}</span>
    </div>

    <div className={spacerStyle} />

    <div className={actionsStyle}>
      {actions}

      {user ? (
        <div className={userStyle}>
          <span className={avatarStyle}>{getInitials(user.name)}</span>
          <div className={userTextStyle}>
            <span className={userNameStyle}>{user.name}</span>
            {user.role ? <span className={userRoleStyle}>{user.role}</span> : null}
          </div>
        </div>
      ) : null}
    </div>
  </header>
);
